import React from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Button, RmBodyZone, SearchNoResultsCard, SearchPageGrid, SearchPageResultGroup, Skeleton, useChat } from 'randmarcomps';

import DataRefreshAssistantCluster from '../components/layout/DataRefreshAssistantCluster';
import RmPageHeader from '../components/layout/RmPageHeader';
import { useManufacturerSearch } from '../components/search/useManufacturerSearch';
import { ManufacturerGridItem } from '../components/search/ManufacturerGridItem';
import { useManufacturer } from '../contexts/WithManufacturer';
import SearchResult from '../stores/search/SearchResult';
import { Product } from 'randmar-api-client';
import withRoot from '../withRoot';

const groupManufacturerResults = (results: SearchResult[], applicationId: string): Record<string, SearchPageResultGroup<SearchResult>> => {
  const products = results.filter(r => r.contentType === 'Product').sort((a, b) => {
    const aRank = (a.newContent as Product).OverallPercentileRank ?? 0;
    const bRank = (b.newContent as Product).OverallPercentileRank ?? 0;
    return bRank - aRank;
  });

  return {
    'Your Products': {
      items: products.filter(r => (r.newContent as Product).ManufacturerId === applicationId),
      gridCols: { base: 1, sm: 2 }
    },
    Opportunities: {
      items: results.filter(r => r.contentType === 'Opportunity'),
      gridCols: { base: 1, sm: 2 }
    },
    Resellers: {
      items: results.filter(r => r.contentType === 'Reseller'),
      gridCols: { base: 1, sm: 2 }
    },
    'Other Brands': {
      items: products.filter(r => (r.newContent as Product).ManufacturerId !== applicationId),
      gridCols: { base: 1, md: 2, lg: 3 }
    },
    Documents: {
      items: results.filter(r => r.contentType === 'Document'),
      gridCols: { base: 1, sm: 2 }
    },
  };
};

const skeletonSections = [
  { title: 'Your Products', gridClassName: 'grid grid-cols-1 gap-4 sm:grid-cols-2', cardCount: 4 },
  { title: 'Opportunities', gridClassName: 'grid grid-cols-1 gap-4 sm:grid-cols-2', cardCount: 2 },
  { title: 'Resellers', gridClassName: 'grid grid-cols-1 gap-4 sm:grid-cols-2', cardCount: 2 },
  { title: 'Other Brands', gridClassName: 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3', cardCount: 6 },
  { title: 'Documents', gridClassName: 'grid grid-cols-1 gap-4 sm:grid-cols-2', cardCount: 3 },
] as const;

const ManufacturerSearchSkeleton: React.FC = () => (
  <div className="space-y-8" role="status" aria-live="polite">
    {skeletonSections.map((section) => (
      <section key={section.title} className="space-y-4" aria-label={`${section.title} loading`}>
        <Skeleton className="h-6 w-44 bg-muted" />
        <div className={section.gridClassName}>
          {Array.from({ length: section.cardCount }).map((_, idx) => (
            <div key={`${section.title}-skeleton-${idx}`} className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <Skeleton className="h-5 w-2/3 bg-muted" />
              <Skeleton className="mt-3 h-4 w-full bg-muted" />
              <Skeleton className="mt-2 h-4 w-5/6 bg-muted" />
              <div className="mt-5 border-t border-border pt-4">
                <Skeleton className="h-9 w-28 bg-muted" />
              </div>
            </div>
          ))}
        </div>
      </section>
    ))}
    <span className="sr-only">Loading search results</span>
  </div>
);

const Search: React.FC = () => {
  const { applicationId } = useManufacturer();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const normalizedQuery = query.trim();
  const hasQuery = normalizedQuery.length > 0;

  const {
    results,
    isInitialLoading,
    isBackgroundFetching,
    reload,
    lastRefreshedAt,
  } = useManufacturerSearch(query, applicationId);

  const { sendMessageWithClear } = useChat();
  const [manualRefreshInFlight, setManualRefreshInFlight] = React.useState(false);
  const [lastSettledQuery, setLastSettledQuery] = React.useState('');
  const [lastSettledResults, setLastSettledResults] = React.useState<SearchResult[]>([]);

  const groupResults = React.useCallback(
    (res: SearchResult[]) => groupManufacturerResults(res, applicationId),
    [applicationId]
  );

  const assistantPrompt = React.useMemo(
    () => hasQuery
      ? `Help me analyze the manufacturer search results for "${normalizedQuery}". Summarize the best matches by group and suggest next actions.`
      : 'Help me search this manufacturer portal and suggest better search queries.',
    [hasQuery, normalizedQuery]
  );

  const handleAssistantClick = React.useCallback(() => {
    sendMessageWithClear(assistantPrompt);
  }, [assistantPrompt, sendMessageWithClear]);

  const handleRefresh = React.useCallback(async () => {
    if (!hasQuery) return;
    setManualRefreshInFlight(true);
    try {
      await reload();
    } finally {
      setManualRefreshInFlight(false);
    }
  }, [hasQuery, reload]);

  React.useEffect(() => {
    if (!hasQuery) {
      setLastSettledQuery('');
      setLastSettledResults([]);
      return;
    }

    if (!isInitialLoading && !isBackgroundFetching) {
      setLastSettledQuery(normalizedQuery);
      setLastSettledResults(results);
    }
  }, [hasQuery, isBackgroundFetching, isInitialLoading, normalizedQuery, results]);

  const isQueryRefinement = React.useMemo(() => {
    if (!hasQuery || !lastSettledQuery) return false;
    const current = normalizedQuery.toLowerCase();
    const previous = lastSettledQuery.toLowerCase();
    return current.includes(previous) || previous.includes(current);
  }, [hasQuery, lastSettledQuery, normalizedQuery]);

  const showPreviousWhileLoading =
    isInitialLoading &&
    isQueryRefinement &&
    !manualRefreshInFlight &&
    lastSettledResults.length > 0;

  const visibleResults = showPreviousWhileLoading ? lastSettledResults : results;
  const showSkeleton = manualRefreshInFlight || (isInitialLoading && !showPreviousWhileLoading);

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = hasQuery
      ? `Search Results for "${normalizedQuery}" | Manufacturer`
      : 'Search Results | Manufacturer';

    return () => {
      document.title = previousTitle;
    };
  }, [hasQuery, normalizedQuery]);

  const noResultsTips = React.useMemo(() => [
    'Check for typos in SKU, brand name, or document identifiers.',
    'Use broader terms first, then narrow down by a specific identifier.',
    'Reload search to confirm newly updated products or documents.',
  ], []);

  return (
    <>
      <RmPageHeader
        title={hasQuery ? `Search Results for "${normalizedQuery}"` : 'Search Results'}
        subtitle="Review matches across products, opportunities, resellers, brands, and documents."
        rightTopActions={(
          <DataRefreshAssistantCluster
            lastRefreshAt={lastRefreshedAt}
            isRefreshing={isBackgroundFetching || manualRefreshInFlight}
            onRefresh={handleRefresh}
            assistantPromptOptions={{ prompt: assistantPrompt }}
          />
        )}
      />
      <RmBodyZone className="space-y-8 pb-10" aria-busy={showSkeleton || isBackgroundFetching}>
        {showSkeleton ? <ManufacturerSearchSkeleton /> : null}

        {!showSkeleton && visibleResults.length > 0 ? (
          <div className="rm-search-results">
            <SearchPageGrid
              results={visibleResults}
              groupBy={groupResults}
              renderItem={(item, index) => (
                <ManufacturerGridItem
                  key={index}
                  searchResult={item}
                  applicationId={applicationId}
                />
              )}
            />
          </div>
        ) : null}

        {!showSkeleton && hasQuery && visibleResults.length === 0 ? (
          <SearchNoResultsCard
            query={normalizedQuery}
            tips={noResultsTips}
            onReloadSearch={handleRefresh}
            onAssistantClick={handleAssistantClick}
          />
        ) : null}

        {!showSkeleton && !hasQuery ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <SearchIcon className="h-7 w-7" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-xl font-semibold text-foreground">Enter a search query</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Use the top navigation search to find products, opportunities, resellers, brands, and documents.
            </p>
            <div className="mt-6 flex items-center justify-center">
              <Button type="button" onClick={handleAssistantClick} className="rounded-xl">
                Ask Assistant
              </Button>
            </div>
          </div>
        ) : null}
      </RmBodyZone>
    </>
  );
};

export default withRoot(Search);
